import type { WeekdayCode } from '@kardume/types';

/**
 * Conversões de data/hora da agenda de experiências (card #347).
 *
 * O core recebe e devolve **instantes com fuso** (ISO 8601 com offset). O
 * lojista, porém, digita relógio de parede: "dia 12, das 20h às 23h". A
 * conversão de um para o outro acontece aqui, uma vez só - `new Date('...T20:00')`
 * é interpretado no fuso do navegador, que é o fuso em que o lojista pensa, e
 * `toISOString()` devolve o mesmo instante escrito em UTC (o `Z` é um offset
 * legítimo). Montar a string à mão sem offset mandaria `LocalDateTime`, que é
 * exatamente o que o card proíbe.
 */

/** `"2026-09-12"` + `"20:00"` -> instante ISO com offset. */
export const toInstant = (date: string, time: string): string =>
  new Date(`${date}T${time}`).toISOString();

/**
 * O término da data, aceitando a **virada da noite**: 22h -> 1h é o dia
 * seguinte, e o core aceita isso (a data faz parte do valor, não há janela
 * partida na meia-noite como no horário de funcionamento).
 */
export const toEndInstant = (date: string, startTime: string, endTime: string): string => {
  const start = new Date(`${date}T${startTime}`);
  const end = new Date(`${date}T${endTime}`);
  if (end.getTime() <= start.getTime()) end.setDate(end.getDate() + 1);
  return end.toISOString();
};

/** Se o término cai no dia seguinte - a tela avisa, para não parecer erro. */
export const endsNextDay = (startTime: string, endTime: string): boolean =>
  startTime !== '' && endTime !== '' && endTime <= startTime;

/** `"12/09/2026 · 20:00 – 23:00"`, no relógio de quem está olhando. */
export const formatSessionRange = (startsAt: string, endsAt: string): string => {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime())) return '-';
  const day = start.toLocaleDateString('pt-BR');
  const time = (value: Date): string =>
    value.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const sameDay = start.toDateString() === end.toDateString();
  return `${day} · ${time(start)} – ${time(end)}${sameDay ? '' : ' (dia seguinte)'}`;
};

/** Uma data já passou? É o que separa "o que ainda dá para cancelar". */
export const isPast = (isoInstant: string): boolean => new Date(isoInstant).getTime() < Date.now();

/** Duração em minutos entre dois instantes - o padrão do campo da recorrência. */
export const durationMinutes = (startsAt: string, endsAt: string): number =>
  Math.max(1, Math.round((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000));

/** Hoje em `"YYYY-MM-DD"`, para o mínimo dos campos de data. */
export const todayIso = (): string => {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Dias da semana na ordem em que o lojista os lê, com o rótulo curto. */
export const RECURRENCE_WEEKDAYS: ReadonlyArray<{ code: WeekdayCode; label: string }> = [
  { code: 'MONDAY', label: 'Seg' },
  { code: 'TUESDAY', label: 'Ter' },
  { code: 'WEDNESDAY', label: 'Qua' },
  { code: 'THURSDAY', label: 'Qui' },
  { code: 'FRIDAY', label: 'Sex' },
  { code: 'SATURDAY', label: 'Sáb' },
  { code: 'SUNDAY', label: 'Dom' },
];
